import React, { useState, useEffect } from 'react';
import { Button, Field, Textarea, Spinner, Dropdown, Option } from '@fluentui/react-components';
import { CheckmarkFilled } from '@fluentui/react-icons';
import { useFileUpload, FILE_KINDS, FILE_REFERENCE_TYPES, FileUploadPayload } from '#root/hooks/useFileUpload';
import { PersonBase, PublicContact, useCreatePerson } from '#root/queries/persons';
import './styles.scss';
import { showToast } from '#root/components/GlobalToaster';
import ButtonGroup from '#root/components/ButtonGroup';
import ImageUploader from '#root/components/ImageUploader';
import { VALIDATION_PATTERNS, VALIDATION_MESSAGES } from '#root/utils/validation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useIncidentById } from '#root/queries/incidents';
import LoadingWrapper from '#root/components/LoadingWrapper';
import { PERSON_ENUMS, TATTOO_LABEL, SCARS_LABEL } from '#root/utils/persons';
import { PERSON_TYPES } from '#root/utils/persons';
import { ROUTES } from '#root/routes/constants';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useIntl } from 'react-intl';
import { SelectionEvents } from "@fluentui/react-components";
import { LANGUAGE_OPTIONS, LANGUAGE_OPTIONS_LIST } from '#root/utils/settings';
import { LocalIntlProvider } from '#root/providers/LanguageProvider';
import { getUnitEnumLabelByUnit } from '#root/utils/enums';
import { withTenantId, useWrappedQuery } from '#root/hooks/useTenantWrappedQuery';
import { useTenantById } from '#root/queries/tenants';
import { Input } from '#root/components/Input';
import { stripPhoneNumber } from '#root/utils/validation';

interface FormData {
    person: PersonBase;
    publicContact: PublicContact;
    personalImage?: File;
    scarImages: File[];
    tattooImages: File[];
}

const initialFormData: FormData = {
    person: {
        firstName: '',
        lastName: '',
        gender: undefined,
        age: undefined,
        height: undefined,
        weight: undefined,
        eyes: undefined,
        hair: undefined,
        features: [],
        additionalInfo: ''
    },
    publicContact: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        relationshipToPerson: undefined
    },
    scarImages: [],
    tattooImages: []
};

interface PublicRequestFormContentProps {
    currentLanguage: string;
    setCurrentLanguage: (lang: string) => void;
}

const PublicRequestFormContent: React.FC<PublicRequestFormContentProps> = ({ 
    currentLanguage, 
    setCurrentLanguage 
}) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const tenantId = searchParams.get('tenantId');
    const incidentId = searchParams.get('incidentId');
    const { executeRecaptcha } = useGoogleReCaptcha();
    const intl = useIntl();

    useEffect(() => {
        if (!tenantId || !incidentId) {
            navigate(ROUTES.ERROR_404);
            return;
        }
    }, [tenantId, incidentId, navigate]);

    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const createPerson = useCreatePerson();
    const { uploadImages } = useFileUpload();
    const { data: incidentResponse, isLoading: incidentLoading } = useWrappedQuery(useIncidentById, { id: incidentId! }, tenantId!);
    const { data: tenantResponse, isLoading: tenantLoading } = useWrappedQuery(useTenantById, { id: tenantId! }, tenantId!);

    const unit = tenantResponse?.item.unit;

    useEffect(() => {
        if (tenantResponse?.item.language) {
            setCurrentLanguage(tenantResponse.item.language);
        }
    }, [tenantResponse?.item.language, setCurrentLanguage]);

    const handlePersonChange = (field: keyof PersonBase, value: any) => {
        setFormData(prev => ({ ...prev, person: { ...prev.person, [field]: value } }));
    };

    const handleContactChange = (field: keyof PublicContact, value: string) => {
        setFormData(prev => ({ ...prev, publicContact: { ...prev.publicContact, [field]: value } }));
    };

    const validateStep1 = () => {
        const { gender, age, height, weight, eyes, hair } = formData.person;
        return gender !== undefined && age !== undefined && height !== undefined && weight !== undefined && eyes !== undefined && hair !== undefined;
    };

    const validateStep2 = () => {
        const { relationshipToPerson } = formData.publicContact;
        return relationshipToPerson !== undefined;
    };

    const verifyRecaptcha = async () => {
        if (!executeRecaptcha) {
            throw new Error('Recaptcha not yet available');
        }

        const token = await executeRecaptcha('public_request_form');
        if (!token) {
            throw new Error('Failed to get reCAPTCHA token');
        }

        return token;
    };

    const submitForm = async () => {
        try {
            setIsSubmitting(true);

            const recaptchaToken = await verifyRecaptcha();

            await withTenantId(async () => {
                const personResponse = await createPerson.mutateAsync({
                    person: {
                        ...formData.person,
                        type: PERSON_TYPES.PUBLIC,
                        incidentId: incidentId!
                    },
                    publicContact: {
                        ...formData.publicContact,
                        phoneNumber: stripPhoneNumber(formData.publicContact.phoneNumber || '')
                    },
                    recaptchaToken: recaptchaToken
                });

                const personId = personResponse.item.id;

                const filesToUpload: FileUploadPayload[] = [];

                if (formData.personalImage) {
                    filesToUpload.push({
                        file: formData.personalImage,
                        referenceId: personId,
                        kind: FILE_KINDS.PERSONAL_IMAGE,
                        referenceType: FILE_REFERENCE_TYPES.PERSON
                    });
                }

                if (formData.person.features) {
                    if (formData.person.features.includes(TATTOO_LABEL.value) && formData.tattooImages.length > 0) {
                        formData.tattooImages.forEach(file => {
                            filesToUpload.push({
                                file,
                                referenceId: personId,
                                kind: FILE_KINDS.TATTOO,
                                referenceType: FILE_REFERENCE_TYPES.PERSON
                            });
                        });
                    }
        
                    if (formData.person.features.includes(SCARS_LABEL.value) && formData.scarImages.length > 0) {
                        formData.scarImages.forEach(file => {
                            filesToUpload.push({
                                file,
                                referenceId: personId,
                                kind: FILE_KINDS.SCAR,
                                referenceType: FILE_REFERENCE_TYPES.PERSON
                            });
                        });
                    }
                }

                if (filesToUpload.length > 0) {
                    await uploadImages(filesToUpload);
                }
            }, tenantId!);

            setIsSubmitted(true);
        } catch (error) {
            showToast('Something went wrong. Please try again or contact the administrator', 'error');
            console.error('Error submitting form:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (step === 1 && validateStep1()) {
            setStep(2);
            return;
        } else if (step === 2 && validateStep2()) {
            await submitForm();
            return;
        }
        showToast("Please fill in all required fields", 'error');
    };

    const renderStep1 = () => (
        <form className="form-step" onSubmit={handleSubmit}>
            <Field label={intl.formatMessage({ id: 'publicForm.incident' })}>
                <Input
                    value={incidentResponse?.item.name}
                    readOnly
                />
            </Field>

            <div className='subtitle'>
                {intl.formatMessage({ id: 'publicForm.personInfo' })} <div className="divider"></div>
            </div>

            <Field label={intl.formatMessage({ id: 'publicForm.firstName' })} required>
                <Input
                    value={formData.person.firstName}
                    onChange={(e) => handlePersonChange('firstName', e.target.value)}
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.lastName' })} required>
                <Input
                    value={formData.person.lastName}
                    onChange={(e) => handlePersonChange('lastName', e.target.value)}
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.gender' })} required>
                <ButtonGroup
                    options={PERSON_ENUMS.GENDER.map(e => ({ label: intl.formatMessage({ id: e.localizedLabel }), value: e.value }))}
                    value={formData.person.gender}
                    onChange={(value) => handlePersonChange('gender', value as string)}
                    name="gender"
                    required
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.ageRange' })} required>
                <ButtonGroup
                    options={PERSON_ENUMS.AGE_RANGES.map(r => ({ label: getUnitEnumLabelByUnit(r, unit!), value: r.value }))}
                    value={formData.person.age}
                    onChange={(value) => handlePersonChange('age', value)}
                    name="ageRange"
                    required
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.heightRange' })} required>
                <ButtonGroup
                    options={PERSON_ENUMS.HEIGHT_RANGES.map(r => ({ label: getUnitEnumLabelByUnit(r, unit!), value: r.value }))}
                    value={formData.person.height}
                    onChange={(value) => handlePersonChange('height', value)}
                    name="heightRange"
                    required
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.weightRange' })} required>
                <ButtonGroup
                    options={PERSON_ENUMS.WEIGHT_RANGES.map(r => ({ label: getUnitEnumLabelByUnit(r, unit!), value: r.value }))}
                    value={formData.person.weight}
                    onChange={(value) => handlePersonChange('weight', value)}
                    name="weightRange"
                    required
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.eyeColor' })} required>
                <ButtonGroup
                    options={PERSON_ENUMS.EYE_COLORS.map(e => ({ label: intl.formatMessage({ id: e.localizedLabel }), value: e.value }))}
                    value={formData.person.eyes}
                    onChange={(value) => handlePersonChange('eyes', value as string)}
                    name="eyeColor"
                    required
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.hairColor' })} required>
                <ButtonGroup
                    options={PERSON_ENUMS.HAIR_COLORS.map(e => ({ label: intl.formatMessage({ id: e.localizedLabel }), value: e.value }))}
                    value={formData.person.hair}
                    onChange={(value) => handlePersonChange('hair', value as string)}
                    name="hairColor"
                    required
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.additionalFeatures' })}>
                <ButtonGroup
                    options={PERSON_ENUMS.FEATURES.map(e => ({ label: intl.formatMessage({ id: e.localizedLabel }), value: e.value }))}
                    value={formData.person.features || []}
                    onChange={(value) => handlePersonChange('features', (value as string[]).map(Number))}
                    name="features"
                    multiple
                />
            </Field>

            {formData.person.features?.includes(TATTOO_LABEL.value) && (
                <Field label={intl.formatMessage({ id: 'publicForm.tattooImages' })}>
                    <ImageUploader
                        placeholder={intl.formatMessage({ id: 'forms.imageUploaderPlaceholder' })}
                        files={formData.tattooImages}
                        onChange={(files) => {
                            if (files.length > 10) {
                                showToast('Maximum 10 images allowed for tattoos', 'error');
                                return;
                            }
                            setFormData(prev => ({
                                ...prev,
                                tattooImages: files
                            }));
                        }}
                        maxFiles={10}
                    />
                </Field>
            )}

            {formData.person.features?.includes(SCARS_LABEL.value) && (
                <Field label={intl.formatMessage({ id: 'publicForm.scarImages' })}>
                    <ImageUploader
                        placeholder={intl.formatMessage({ id: 'forms.imageUploaderPlaceholder' })}
                        files={formData.scarImages}
                        onChange={(files) => {
                            if (files.length > 10) {
                                showToast('Maximum 10 images allowed for scars', 'error');
                                return;
                            }
                            setFormData(prev => ({
                                ...prev,
                                scarImages: files
                            }));
                        }}
                        maxFiles={10}
                    />
                </Field>
            )}

            <Field label={intl.formatMessage({ id: 'publicForm.additionalInfo' })}>
                <Textarea
                    value={formData.person.additionalInfo}
                    onChange={(e) => handlePersonChange('additionalInfo', e.target.value)}
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.personalImage' })}>
                <div className="description">{intl.formatMessage({ id: 'publicForm.personalImageDescription' })}</div>
                <ImageUploader
                    placeholder={intl.formatMessage({ id: 'forms.imageUploaderPlaceholder' })}
                    files={formData.personalImage ? [formData.personalImage] : []}
                    onChange={(files) =>
                        setFormData(prev => ({
                            ...prev,
                            personalImage: files[0]
                        }))
                    }
                    maxFiles={1}
                />
            </Field>

            <div className="emc-navigation">
                <Button size="large" type="submit" appearance="primary">
                    {intl.formatMessage({ id: 'publicForm.nextStep' })}
                </Button>
            </div>
        </form>
    );

    const renderStep2 = () => (
        <form className={`form-step${isSubmitting ? ' submitting' : ''}`} onSubmit={handleSubmit}>
            <div className='subtitle'>
                {intl.formatMessage({ id: 'publicForm.yourInfo' })} <div className="divider"></div>
            </div>

            <Field label={intl.formatMessage({ id: 'publicForm.yourFirstName' })} required>
                <Input
                    value={formData.publicContact.firstName}
                    onChange={(e) => handleContactChange('firstName', e.target.value)}
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.yourLastName' })} required>
                <Input
                    value={formData.publicContact.lastName}
                    onChange={(e) => handleContactChange('lastName', e.target.value)}
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.yourEmail' })} required>
                <Input
                    type="email"
                    value={formData.publicContact.email}
                    onChange={(e) => handleContactChange('email', e.target.value)}
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.yourPhoneNumber' })} required>
                <Input
                    type="tel"
                    value={formData.publicContact.phoneNumber}
                    onChange={(e) => handleContactChange('phoneNumber', e.target.value)}
                    required
                    pattern={VALIDATION_PATTERNS.PHONE}
                    title={VALIDATION_MESSAGES.PHONE}
                />
            </Field>

            <Field label={intl.formatMessage({ id: 'publicForm.relationshipToPerson' })} required>
                <ButtonGroup
                    options={PERSON_ENUMS.RELATIONSHIPS.map(e => ({ label: intl.formatMessage({ id: e.localizedLabel }), value: e.value }))}
                    value={formData.publicContact.relationshipToPerson}
                    onChange={(value) => handleContactChange('relationshipToPerson', value as string)}
                    name="relationshipToPerson"
                    required
                />
            </Field>

            <div className="emc-navigation">
                <Button size="large" appearance="secondary" onClick={() => setStep(1)}>
                    {intl.formatMessage({ id: 'publicForm.previousStep' })}
                </Button>
                <Button
                    size="large"
                    appearance="primary"
                    type="submit"
                    disabled={isSubmitting}
                    icon={isSubmitting ? <Spinner size="tiny" /> : null}
                >
                    {isSubmitting
                        ? intl.formatMessage({ id: 'publicForm.submitting' })
                        : intl.formatMessage({ id: 'publicForm.submit' })}
                </Button>
            </div>
        </form>
    );

    const renderConfirmation = () => (
        <div className="confirmation">
            <CheckmarkFilled className="success-icon" />
            <div className='subtitle'>{intl.formatMessage({ id: 'publicForm.submissionCompleted' })}</div>
        </div>
    );

    const handleLanguageChange = (_: SelectionEvents, data: { selectedOptions: string[] }) => {
        const value = data.selectedOptions[0];
        if (value) {
            setCurrentLanguage(value);
        }
    };

    return (
        <LoadingWrapper
            isLoading={incidentLoading || tenantLoading}
            isEmpty={!incidentResponse?.item}
            emptyMessage={intl.formatMessage({ id: 'publicForm.incidentNotFound' })}
        >
            <div className="public-request-form">
                {!isSubmitted ? (
                    <div className="public-request-form__header">
                        <Field label={intl.formatMessage({ id: 'publicForm.language' })} className="language-field">
                            <Dropdown value={currentLanguage} onOptionSelect={handleLanguageChange}>
                                {LANGUAGE_OPTIONS_LIST.map(lang => (
                                    <Option key={lang} value={lang}>
                                        {lang}
                                    </Option>
                                ))}
                            </Dropdown>
                        </Field>
                        <p className="instructions">{intl.formatMessage({ id: 'publicForm.instructions' })}</p>
                        <p>{intl.formatMessage({ id: 'publicForm.step' }, { step, steps: 2 })}</p>
                        {step === 1 ? renderStep1() : renderStep2()}
                    </div>
                ) : (
                    renderConfirmation()
                )}
            </div>
        </LoadingWrapper>
    );
};

const PublicRequestForm: React.FC = () => {
    const [currentLanguage, setCurrentLanguage] = useState<string>(LANGUAGE_OPTIONS.ENGLISH);

    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}
            scriptProps={{
                async: true,
                defer: true,
                appendTo: 'head'
            }}
        >
            <LocalIntlProvider language={currentLanguage}>
                <PublicRequestFormContent currentLanguage={currentLanguage} setCurrentLanguage={setCurrentLanguage} />
            </LocalIntlProvider>
        </GoogleReCaptchaProvider>
    );
};

export default PublicRequestForm;
